import { Link } from "react-router-dom";
import { Award, RefreshCw, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { CERTS, CERT_PROCESS } from "../lib/content";
import { useContent } from "../context/ContentContext";
import HeroBackdrop from "../components/HeroBackdrop";

export default function Certification() {
  const { t } = useContent();
  return (
    <div data-testid="certification-page">
      <section className="relative overflow-hidden bg-[#0F2744] py-20 lg:py-28">
        <HeroBackdrop sectionKey="certification" image={t("img.certification", "https://customer-assets-39nsmqrw.emergentagent.net/job_rospa-wm-portal/artifacts/uca9y63s_image.png")} />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="eyebrow">RoSPA test & certification</span>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white max-w-3xl leading-tight">{t("certification.title", "Advanced standard, nationally respected")}</h1>
          <p className="mt-5 text-lg text-slate-200 max-w-2xl">{t("certification.subtitle", "The RoSPA advanced test sets a genuinely high bar. It's graded Bronze, Silver or Gold, and you re-test every three years — so your skills stay sharp.")}</p>
        </div>
      </section>

      {/* Grades */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <span className="eyebrow">The grades</span>
        <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-[#0F2744]">Bronze, Silver & Gold</h2>
        <div className="mt-10 grid md:grid-cols-3 gap-6">
          {CERTS.map((c) => (
            <div key={c.tier} className="rounded-2xl bg-white border border-slate-200 p-7 hover:shadow-lg transition-shadow" data-testid={`grade-${c.tier.toLowerCase()}`}>
              <div className="h-14 w-14 rounded-2xl flex items-center justify-center mb-5" style={{ background: c.color }}>
                <Award className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-[#0F2744]">{c.tier}</h3>
              <p className="mt-3 text-slate-600 leading-relaxed">{c.summary}</p>
              <ul className="mt-5 space-y-2">
                {c.points.map((pt, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0" style={{ background: c.color }} />
                    {pt}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Process */}
      <section className="bg-slate-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="eyebrow">The assessment process</span>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-[#0F2744]">From training to test day</h2>
          <div className="mt-10 space-y-4">
            {CERT_PROCESS.map((step, i) => (
              <div key={i} className="flex items-start gap-5 rounded-2xl bg-white border border-slate-200 p-6" data-testid={`process-${i}`}>
                <div className="h-11 w-11 rounded-full bg-[#0F2744] text-white font-heading font-bold flex items-center justify-center shrink-0">{i + 1}</div>
                <div>
                  <h3 className="font-bold text-[#0F2744] text-lg">{step.title}</h3>
                  <p className="mt-1 text-slate-600">{step.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Retest */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <div className="inline-flex h-14 w-14 rounded-2xl bg-amber-100 items-center justify-center mb-5">
          <RefreshCw className="h-7 w-7 text-amber-600" />
        </div>
        <h2 className="text-3xl font-bold text-[#0F2744]">Retesting & progression</h2>
        <p className="mt-4 text-lg text-slate-600">Unlike a one-off qualification, the RoSPA grade is re-assessed every three years. It keeps your standard high and gives you a clear goal to work towards — whether maintaining Gold or climbing from Bronze to Silver to Gold.</p>
        <Button asChild size="lg" className="mt-8 bg-amber-500 hover:bg-amber-600 text-[#0F2744] font-bold h-14 px-10">
          <Link to="/join" data-testid="cert-join-btn">Begin your journey <ArrowRight className="h-4 w-4 ml-2" /></Link>
        </Button>
      </section>
    </div>
  );
}
